/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { isNil, isEmpty } from 'lodash';
import { DbStore } from 'crimson/lib/db/models/db.models';
import { SharedStorageService } from '../../../../shared-storage/src/lib/shared-storage.service';
import {
    UnauthenticatedSession,
    StarfleetAuthProvider,
    StarfleetStoreDetails,
    StorageType
} from '../constants/constants';
import { SessionData } from '../starfleet.interface';

export class EncodedData {
    public data: string;
    public authProvider: string;
    /**
     * Encodes storageSession
     *
     * @param storageSession
     * @return object that contains encoded session
     */
    public static encode(data: SessionData) {
        const encodedData: EncodedData = new EncodedData();
        // We are calling encodeURIComponent to support backwards compatibility.
        // TODO: Once we are fully on ng6 we should reevaluate if this is actually
        // necessary. https://jirasw.nvidia.com/browse/MVPC-2943
        encodedData.data = window.btoa(encodeURIComponent(JSON.stringify(data)));
        encodedData.authProvider = StarfleetAuthProvider;
        return encodedData;
    }

    /**
     * Decodes sessionData and returns StorageSession
     *
     * @param sessionData
     * @returns session object
     */
    public static decode(encodedData: EncodedData): SessionData {
        const decodedData: SessionData = JSON.parse(decodeURIComponent(window.atob(encodedData.data)));
        return decodedData;
    }
}

export class Session {
    public data: SessionData;
    public storageType: StorageType;

    public constructor(
        private idTokenRefreshUnderDuration: number,
        sessionData?: SessionData,
        storageType: StorageType = StorageType.SharedStorage
    ) {
        if (!isNil(sessionData)) {
            this.data = sessionData;
        } else {
            this.data = Object.assign({}, UnauthenticatedSession);
        }
        this.setStorageType(storageType);
    }

    public static loadFrom(
        storage: SharedStorageService | DbStore,
        idTokenRefreshUnderDuration: number
    ): Observable<Session> {
        const node = 'starfleetSession';
        let storageType: StorageType;
        if (storage) {
            storageType = storage instanceof SharedStorageService ? StorageType.SharedStorage : StorageType.IndexedDB;
        } else {
            storageType = StorageType.SharedStorage;
        }
        const readBlock$ =
            storageType === StorageType.IndexedDB
                ? (storage as DbStore).getItem(StarfleetStoreDetails.DBKey)
                : (storage as SharedStorageService).readBlock<EncodedData>(node);

        return readBlock$.pipe(
            mergeMap((encodedData: EncodedData) => {
                const sessionData =
                    encodedData?.authProvider === StarfleetAuthProvider ? EncodedData.decode(encodedData) : null;
                const session = new Session(idTokenRefreshUnderDuration, sessionData, storageType);
                if (isEmpty(sessionData)) {
                    return storageType === StorageType.IndexedDB
                        ? session.writeToDb(storage as DbStore)
                        : session.writeTo(storage as SharedStorageService);
                }
                return of(session);
            })
        );
    }

    setStorageType(storageType: StorageType): void {
        if (storageType === StorageType.IndexedDB) {
            this.storageType = StorageType.IndexedDB;
        } else {
            this.storageType = StorageType.SharedStorage;
        }
    }

    public writeTo(storage: SharedStorageService | DbStore): Observable<Session> {
        if (this.storageType === StorageType.IndexedDB) {
            return this.writeToDb(storage as DbStore);
        } else {
            const node = 'starfleetSession';
            return (storage as SharedStorageService)
                .writeBlock(node, EncodedData.encode(this.data), true)
                .pipe(map(() => this));
        }
    }

    public writeToDb(sessionStore: DbStore): Observable<Session> {
        const writeDb$ = sessionStore.setItem(StarfleetStoreDetails.DBKey, EncodedData.encode(this.data));
        writeDb$.subscribe();
        return of(this);
    }

    public hasClientToken(): boolean {
        return !isNil(this.data.clientToken);
    }

    public isClientTokenValid(): boolean {
        return this.hasClientToken() && this.data.clientTokenExpiry > Date.now();
    }

    public hasClientTokenExpired(): boolean {
        return this.hasClientToken() && this.data.clientTokenExpiry <= Date.now();
    }

    public hasAccessToken(): boolean {
        return !isNil(this.data.accessToken);
    }

    public isAccessTokenValid(): boolean {
        return this.hasClientToken() && this.data.accessTokenExpiry > Date.now();
    }

    public isIdTokenValid(): boolean {
        if (this.data.idTokenExpiry === 0) {
            return true;
        }

        return this.hasClientToken() && this.data.idTokenExpiry > Date.now() + this.idTokenRefreshUnderDuration;
    }
}
